import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Previous.css';
import Modal from '../components/Modal';
import LoadingOverlay from '../components/LoadingOverlay';
import TransactionSearch from '../components/TransactionSearch';
import { api } from '../services/db';
import { useAuth } from '../context/AuthContext';

// Shared helper: groups raw transaction documents into card-groups by transaction_code
function groupDocuments(data) {
  const grouped = new Map();
  data.forEach(tx => {
    if (tx.the_status === 'pending') return;
    const key = tx.transaction_code || tx.$id;
    let dbStatus = tx.status;
    let displayStatus = '';
    if (dbStatus === 'completed') displayStatus = 'كامل';
    if (dbStatus === 'not_completed') displayStatus = 'متبقي';
    if (!displayStatus && tx.type === 'out') {
      const match = tx.notes.match(/\((.*?)\)/);
      if (match) displayStatus = match[1];
    }
    let name = tx.notes;
    if (name.startsWith('منصرف:') || name.startsWith('وارد:')) {
      if (tx.type === 'out') {
        const m = name.match(/منصرف:\s*(.*?)\s*\(/);
        if (m) name = m[1];
      } else {
        const m = name.match(/وارد:\s*(.*?)\s*-/);
        if (m) name = m[1];
      }
    }
    if (!grouped.has(key)) {
      grouped.set(key, {
        id: key,
        code: tx.transaction_code || '---',
        date: tx.date,
        createdAt: tx.$createdAt || '',
        type: tx.type,
        name: name,
        statusEnum: dbStatus,
        statusDisplay: displayStatus,
        userName: tx.user_name || '---',
        items: []
      });
    }
    const group = grouped.get(key);
    group.items.push({
      id: tx.$id,
      name: tx.item_id,
      qty: tx.quantity,
      received: tx.received || 0,
      remaining: tx.remaining || 0,
      consumed_50kg: tx.consumed_50kg || 0,
      the_item_id: tx.the_item_id
    });
  });
  return Array.from(grouped.values());
}

export default function Previous() {
  const { user, warehouseId, teamRoles } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit State
  const [editingGroup, setEditingGroup] = useState(null);
  const [editForm, setEditForm] = useState(null);

  // Feedback/Confirm Modal
  const [modal, setModal] = useState({ isOpen: false, message: '', type: 'success', onConfirm: null });
  const [isProcessing, setIsProcessing] = useState(false);

  // Search State
  const [searchMode, setSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchOffset, setSearchOffset] = useState(0);
  const [lastSearchParams, setLastSearchParams] = useState(null);
  const [displayedCodes, setDisplayedCodes] = useState(new Set());

  useEffect(() => {
    // Guard
    if (teamRoles.includes('production')) {
      navigate('/production');
      return;
    }

    if (warehouseId) loadData();
    // eslint-disable-next-line
  }, [warehouseId, teamRoles]);


  // Reload on Back Button if Warning is Open
  useEffect(() => {
    const handlePopState = () => {
      if (modal.isOpen && modal.type === 'warning') {
        window.location.reload();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [modal]);

  async function loadData() {
    setLoading(true);
    const data = await api.getTodayTransactions(warehouseId);
    setGroups(groupDocuments(data));
    setLoading(false);
  }

  // === SEARCH HANDLERS (only triggered on explicit confirm) ===
  const PAGE_SIZE = 150;

  async function handleSearch(params) {
    setSearchLoading(true);
    setSearchMode(true);
    setSearchOffset(0);
    setDisplayedCodes(new Set());
    setLastSearchParams(params);

    try {
      const result = await api.searchTransactions(warehouseId, {
        ...params,
        offset: 0,
        pageSize: PAGE_SIZE,
      });

      const newGroups = groupDocuments(result.documents);
      const newCodes = new Set(newGroups.map(g => g.code));

      setSearchResults(newGroups);
      setDisplayedCodes(newCodes);
      setSearchHasMore(result.hasMore);
      setSearchOffset(result.fetchedRowCount);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setSearchHasMore(false);
    } finally {
      setSearchLoading(false);
    }
  }

  async function handleLoadMore() {
    if (!lastSearchParams || searchLoading) return;
    setSearchLoading(true);

    try {
      const result = await api.searchTransactions(warehouseId, {
        ...lastSearchParams,
        offset: searchOffset,
        pageSize: PAGE_SIZE,
      });

      const newGroups = groupDocuments(result.documents);
      const uniqueNewGroups = newGroups.filter(g => !displayedCodes.has(g.code));
      const updatedCodes = new Set(displayedCodes);
      uniqueNewGroups.forEach(g => updatedCodes.add(g.code));

      setSearchResults(prev => [...prev, ...uniqueNewGroups]);
      setDisplayedCodes(updatedCodes);
      setSearchHasMore(result.hasMore);
      setSearchOffset(prev => prev + result.fetchedRowCount);
    } catch (error) {
      console.error('Load more error:', error);
    } finally {
      setSearchLoading(false);
    }
  }

  function handleClearSearch() {
    setSearchMode(false);
    setSearchResults([]);
    setSearchHasMore(false);
    setSearchOffset(0);
    setLastSearchParams(null);
    setDisplayedCodes(new Set());
  }

  // Which groups to render: search results or today's groups
  const displayGroups = searchMode ? searchResults : groups;

  function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-GB');
  }

  function formatTime(timestamp) {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? 'م' : 'ص'; // م for PM, ص for AM

    // Convert to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12

    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${hours}:${minutesStr} ${period}`;
  }

  function openEdit(group) {
    // Check permissions
    // Check permissions
    if (teamRoles.includes('manager')) {
      setModal({ isOpen: true, message: 'غير مسموح لك بالتعديل.', type: 'error' });
      return;
    }

    if (group.type === 'production') {
      if (!teamRoles.includes('admin') && !teamRoles.includes('owner')) {
        setModal({ isOpen: true, message: 'غير مسموح لك بتعديل سجلات الإنتاج.', type: 'error' });
        return;
      }
    }

    setEditingGroup(group);
    setEditForm({
      ...group,
      items: group.items.map(i => ({ ...i })) // Deep copy items
    });
  }

  function handleEditChange(field, value) {
    setEditForm(prev => {
      const newState = { ...prev, [field]: value };

      // Auto-update items if status changes (Standard Logic)
      if (field === 'statusDisplay' && prev.type !== 'production') {
        if (value === 'كامل') {
          newState.items = prev.items.map(it => ({ ...it, received: it.qty, remaining: 0 }));
        } else if (value === 'متبقي') {
          newState.items = prev.items.map(it => ({ ...it, received: 0, remaining: it.qty }));
        }
      }
      return newState;
    });
  }

  function handleItemChange(idx, field, value) {
    setEditForm(prev => {
      const newItems = [...prev.items];

      // PRODUCTION LOGIC: Auto-calc consumed
      if (prev.type === 'production' && field === 'qty') {
        const item = { ...newItems[idx], qty: value };
        const q = parseInt(value) || 0;
        let sackSize = 5;
        if (item.name.includes('10')) sackSize = 10;

        const totalW = q * sackSize;
        item.consumed_50kg = Math.ceil(totalW / 50);

        newItems[idx] = item;
        return { ...prev, items: newItems };
      }

      // STANDARD LOGIC
      const item = { ...newItems[idx], [field]: value };
      if (prev.type !== 'production' && (field === 'qty' || field === 'received')) {
        const q = parseInt(item.qty) || 0;
        let r = 0;

        if (field === 'received') {
          if (value === '') {
            item.received = '';
            r = 0;
          } else {
            r = parseInt(value) || 0;
            if (r > q) r = q;
            if (r < 0) r = 0;
            item.received = r;
          }
        } else {
          // field is qty
          if (item.received === '') {
            r = 0;
          } else {
            r = parseInt(item.received) || 0;
            if (r > q) r = q;
            item.received = r;
          }
        }
        item.remaining = q - r;
      }

      newItems[idx] = item;
      return { ...prev, items: newItems };
    });
  }

  function removeItem(idx) {
    setEditForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx)
    }));
  }

  async function saveEdit() {
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 100)); // Force render
    try {
      // Handle empty items -> Delete Entire Transaction
      if (editForm.items.length === 0) {
        setModal({
          isOpen: true,
          type: 'warning',
          message: 'هل انت متأكد من حذف هذه المعاملة ؟ لقد قمت بإزالة جميع الاصناف من المعاملة',
          onConfirm: async () => {
            setModal({ ...modal, isOpen: false });
            await proceedSave();
          }
        });
        setIsProcessing(false);
        return;
      }

      await proceedSave();
    } catch (error) {
      setModal({ isOpen: true, message: error.message || 'فشل التحديث', type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  }

  async function proceedSave() {
    setIsProcessing(true);
    try {
      // Validate (only if not deleting)
      if (editForm.items.length > 0) {
        for (const it of editForm.items) {
          if (!it.name) throw new Error('يرجى تحديد الصنف لجميع الصفوف.');
          if (!it.qty || Number(it.qty) <= 0) throw new Error('يرجى تحديد الكمية لجميع الصفوف.');
        }
      }

      const newNotes = editForm.type === 'production' ? editForm.name : editForm.name; // Keep name as notes for generic

      let statusEnum = null;
      if (editForm.type === 'out') {
        statusEnum = editForm.statusDisplay === 'كامل' ? 'completed' : 'not_completed';
      }
      if (editForm.type === 'production') statusEnum = 'completed';

      const payload = {
        code: editForm.code,
        type: editForm.type,
        date: editForm.date,
        notes: editForm.type === 'production' ? editForm.name : newNotes, // Use current 'name' field as 'notes' for production
        status: statusEnum,
        items: editForm.items
      };

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('UI_TIMEOUT')), 20000)
      );

      await Promise.race([
        api.executeEditTransaction(editingGroup, payload, warehouseId),
        timeoutPromise
      ]);

      setModal({
        isOpen: true,
        message: editForm.items.length === 0 ? 'تم حذف المعاملة بنجاح' : 'تم تحديث المعاملة بنجاح',
        type: 'success',
        onConfirm: null
      });
      setEditingGroup(null);
      // Refresh: re-run search if in search mode, otherwise reload today
      if (searchMode && lastSearchParams) {
        handleSearch(lastSearchParams);
      } else {
        loadData();
      }
    } catch (error) {
      if (error.message === 'UI_TIMEOUT') {
        setModal({
          isOpen: true,
          message: 'حدث خطاء في الشبكة ، تفقد المعاملات السابقة للتأكد من نجاح العملية او فشلها قبل المحاولة مرة اخرى',
          type: 'warning',
          onConfirm: null
        });
      } else {
        setModal({ isOpen: true, message: error.message || 'فشل التحديث', type: 'error', onConfirm: null });
      }
    } finally {
      setIsProcessing(false);
    }
  }

  // Helper checks for permissions
  const isAdmin = teamRoles.includes('admin') || teamRoles.includes('owner');
  const isManager = teamRoles.includes('manager');

  return (
    <div className="previous-root" dir="rtl">
      <LoadingOverlay isLoading={isProcessing || loading} />
      <Modal
        isOpen={modal.isOpen}
        message={modal.message}
        type={modal.type}
        onClose={() => {
          if (modal.type === 'warning') {
            window.location.reload();
          } else {
            setModal({ ...modal, isOpen: false });
          }
        }}
        onConfirm={modal.onConfirm}
      />

      {/* Edit Modal Overlay */}
      {editingGroup && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <h2>تعديل المعاملة ({editingGroup.code})</h2>

            <div className="form-row">
              <label>التاريخ:</label>
              <input type="text" value={formatDate(editForm.date)} readOnly disabled />
            </div>

            {/* Production Specific Edit Form */}
            {editForm.type === 'production' ? (
              <>
                <div className="form-row">
                  <label>ملاحظات الإنتاج:</label>
                  <textarea
                    value={editForm.name}
                    onChange={e => handleEditChange('name', e.target.value)}
                    rows="3"
                    maxLength={500}
                    style={{ fontSize: '16px', resize: 'none' }} // Bigger font, no resize
                  />
                </div>
              </>
            ) : (
              /* Standard Edit Form */
              editForm.type === 'in' ? (
                <div className="form-row">
                  <label>شهادة الوارد/الجهة:</label>
                  <input value={editForm.name} readOnly disabled style={{ background: '#eee' }} />
                </div>
              ) : (
                <>
                  <div className="form-row">
                    <label>اسم المستلم:</label>
                    <input value={editForm.name} readOnly disabled style={{ background: '#eee' }} />
                  </div>
                  <div className="form-row">
                    <label>حالة الاستلام:</label>
                    <select value={editForm.statusDisplay} onChange={e => handleEditChange('statusDisplay', e.target.value)}>
                      <option value="كامل">كامل</option>
                      <option value="متبقي">متبقي</option>
                    </select>
                  </div>
                </>
              )
            )}

            <h3>الأصناف</h3>
            <div className="edit-items-list">
              {editForm.items.map((it, idx) => (
                <div key={idx} className="item-row">
                  <input type="text" value={it.name} readOnly disabled style={{ flex: 2, background: '#eee' }} />
                  <input type="number" placeholder="الكمية" value={it.qty} onChange={e => handleItemChange(idx, 'qty', e.target.value)} style={{ flex: 1 }} />

                  {/* Production Extra Field - Editable and Aligned */}
                  {editForm.type === 'production' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 10 }}>
                      <label style={{ fontSize: '16px', fontWeight: 'bold' }}>استهلاك 50ك</label>
                      <input
                        type="number"
                        value={it.consumed_50kg}
                        onChange={e => handleItemChange(idx, 'consumed_50kg', e.target.value)}
                        style={{ width: 80, fontSize: '16px' }}
                      />
                    </div>
                  )}

                  {/* Standard Partial Fields */}
                  {editForm.type !== 'production' && editForm.statusDisplay === 'متبقي' && (
                    <>
                      <hr className='edit-items-list-hr' />
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        <label style={{ fontSize: '0.8rem', marginLeft: 2 }}>مستلم:</label>
                        <input
                          type="number"
                          value={it.received}
                          onChange={e => handleItemChange(idx, 'received', e.target.value)}
                          style={{ width: 60, background: '#e8f5e9' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        <label style={{ fontSize: '0.8rem', marginLeft: 2 }}>متبقي:</label>
                        <input type="number" value={it.remaining} readOnly disabled style={{ width: 60, background: '#e0e0e0', color: '#555' }} />
                      </div>
                    </>
                  )}

                  {editForm.type !== 'production' && (
                    <button type="button" className="remove-btn" onClick={() => removeItem(idx)}>-</button>
                  )}
                </div>
              ))}
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn" style={{ background: '#2ecc71' }} onClick={saveEdit}>حفظ</button>
              <button className="btn" style={{ background: '#95a5a6' }} onClick={() => setEditingGroup(null)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}

      <div><Link to="/home"><button className="back-btn">الرجوع</button></Link></div>
      <div className="container">
        <h1>{searchMode ? `نتائج البحث (${displayGroups.length})` : `معاملات اليوم (${displayGroups.length})`}</h1>

        {/* Search Component */}
        <TransactionSearch
          onSearch={handleSearch}
          onClear={handleClearSearch}
          isSearching={searchLoading}
          isSearchMode={searchMode}
          resultCount={displayGroups.length}
          hasMore={searchHasMore}
          onLoadMore={handleLoadMore}
        />

        {(loading || searchLoading) ? <p>جاري التحميل...</p> : (
          <>
            <div id="transactions-list">
              {displayGroups.length === 0 ? (
                <p style={{ textAlign: 'center', padding: 20, color: '#888' }}>
                  {searchMode ? 'لا توجد نتائج مطابقة' : 'لا توجد معاملات اليوم'}
                </p>
              ) :
                displayGroups.map((group) => (
                  <div className="tx-card" key={group.id} style={{
                    borderLeft: group.type === 'production' ? '5px solid #9b59b6' :
                      group.type === 'out' ? '5px solid #c51a07ff' :
                        group.type === 'in' ? '5px solid #2ecc71' : undefined
                  }}>
                    <div style={{ position: 'relative' }}>

                      {(
                        !isManager && (
                          (group.type === 'production' && isAdmin) ||
                          (group.type !== 'production' && !group.code?.startsWith('P-'))
                        )
                      ) && (
                          <button className="edit-btn-top-left" onClick={() => openEdit(group)}>تعديل</button>
                        )}

                      <div className="tx-meta" style={{ marginTop: 25 }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <strong>الكود: {group.code}</strong>
                          </div>
                          <div><strong>التاريخ:</strong> {formatDate(group.date)}</div>
                          <div><strong>الوقت:</strong> {formatTime(group.createdAt)}</div>

                          <div>
                            <strong>النوع:</strong> {
                              group.type === 'in' ? 'وارد' :
                                group.type === 'production' ? 'إنتاج' : 'منصرف'
                            }
                            {group.type === 'out' && group.statusDisplay && ` (${group.statusDisplay})`}
                          </div>

                          <div>
                            <strong>{
                              group.type === 'in' ? 'الجهة:' :
                                group.type === 'production' ? 'ملاحظات:' : 'المستلم:'
                            }</strong> {group.name}
                          </div>

                          <div>
                            <strong>المستخدم:</strong> {group.userName}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="tx-items">
                      <table>
                        <thead>
                          <tr>
                            <th>الصنف</th>
                            <th>الكمية</th>
                            {group.type === 'production' && <th>استهلاك 50ك</th>}
                            {group.type !== 'in' && group.type !== 'production' && group.statusDisplay !== 'كامل' && (
                              <>
                                <th>مستلم</th>
                                <th>متبقي</th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {group.items.map(it => (
                            <tr key={it.id}>
                              <td>{it.name}</td>
                              <td>{it.qty}</td>
                              {group.type === 'production' && <td>{it.consumed_50kg}</td>}
                              {group.type !== 'in' && group.type !== 'production' && group.statusDisplay !== 'كامل' && (
                                <>
                                  <td>{it.received}</td>
                                  <td>{it.remaining}</td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
            </div>

            {/* Load More button */}
            {searchMode && searchHasMore && !searchLoading && (
              <div style={{ textAlign: 'center', margin: '20px 0' }}>
                <button className="tx-search-loadmore-btn" onClick={handleLoadMore}>
                  تحميل المزيد
                </button>
              </div>
            )}

            {!searchMode && (
              <div style={{ borderTop: '1px solid #ddd', marginTop: 30, paddingTop: 20, textAlign: 'center' }}>
                <Link to="/previous-all">
                  <button style={{
                    background: '#34495e',
                    color: 'white',
                    padding: '12px 24px',
                    fontSize: '1rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: 'none',
                    width: '100%',
                    fontWeight: 'bold'
                  }}>
                    جميع المعاملات السابقة
                  </button>
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
